import React, { useState } from 'react';
import { 
  ShoppingCart, AlertTriangle, Clock, DollarSign,
  CheckCircle, XCircle, Calendar, TrendingUp
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface OrderSuggestion {
  skuId: string;
  skuName: string;
  currentStock: number;
  suggestedQuantity: number;
  urgency: 'immediate' | 'today' | 'this_week' | 'next_week';
  reason: string;
  estimatedCost: number;
  expectedDelivery: string;
}

interface StockItem {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  optimalStock: number;
  minStock: number;
  maxStock: number;
  dailyDemand: number;
  leadTimeDays: number;
  reorderPoint: number;
  orderQuantity: number;
  stockDays: number;
  status: 'optimal' | 'low' | 'critical' | 'excess';
}

interface OrderProposalProps {
  suggestions: OrderSuggestion[];
  stockItems: StockItem[];
}

const OrderProposal: React.FC<OrderProposalProps> = ({ suggestions, stockItems }) => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [orderStatus, setOrderStatus] = useState<Record<string, 'pending' | 'approved' | 'rejected'>>({});

  const handleSelectOrder = (skuId: string) => {
    setSelectedOrders(prev => 
      prev.includes(skuId) 
        ? prev.filter(id => id !== skuId)
        : [...prev, skuId]
    );
  };

  const handleApproveOrder = (skuId: string) => {
    setOrderStatus(prev => ({ ...prev, [skuId]: 'approved' }));
  };

  const handleRejectOrder = (skuId: string) => {
    setOrderStatus(prev => ({ ...prev, [skuId]: 'rejected' }));
  };

  const handleBatchApprove = () => {
    const newStatus = { ...orderStatus };
    selectedOrders.forEach(id => {
      newStatus[id] = 'approved';
    });
    setOrderStatus(newStatus);
    setSelectedOrders([]);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return colors.status.error;
      case 'today': return colors.status.warning;
      case 'this_week': return colors.accent.blue;
      default: return colors.text.secondary;
    }
  };

  const getUrgencyText = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return '即時';
      case 'today': return '本日中';
      case 'this_week': return '今週中';
      case 'next_week': return '来週';
      default: return urgency;
    }
  };

  // 緊急度別にグループ化
  const groupedSuggestions = suggestions.reduce((acc, suggestion) => {
    if (!acc[suggestion.urgency]) {
      acc[suggestion.urgency] = [];
    }
    acc[suggestion.urgency].push(suggestion);
    return acc;
  }, {} as Record<string, OrderSuggestion[]>);

  // 合計金額計算
  const selectedTotal = selectedOrders.reduce((sum, id) => {
    const suggestion = suggestions.find(s => s.skuId === id);
    return sum + (suggestion?.estimatedCost || 0);
  }, 0);

  const approvedTotal = Object.entries(orderStatus)
    .filter(([_, status]) => status === 'approved')
    .reduce((sum, [id, _]) => {
      const suggestion = suggestions.find(s => s.skuId === id);
      return sum + (suggestion?.estimatedCost || 0);
    }, 0);

  return (
    <div className="space-y-6">
      {/* サマリー */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} style={{ color: colors.status.error }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {suggestions.filter(s => s.urgency === 'immediate').length}件
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                緊急発注
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <ShoppingCart size={20} style={{ color: colors.accent.blue }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                {suggestions.length}件
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                発注提案
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <DollarSign size={20} style={{ color: colors.support.success }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                ¥{(selectedTotal / 1000).toFixed(0)}K
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                選択金額
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} style={{ color: colors.support.success }} />
            <div>
              <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
                ¥{(approvedTotal / 1000).toFixed(0)}K
              </div>
              <div className="text-xs" style={{ color: colors.text.secondary }}>
                承認済金額
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 一括操作ボタン */}
      {selectedOrders.length > 0 && (
        <Card className="p-4 border-l-4" style={{ borderLeftColor: colors.accent.blue }}>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium" style={{ color: colors.text.primary }}>
                {selectedOrders.length}件選択中
              </span>
              <span className="ml-3 text-sm" style={{ color: colors.text.secondary }}>
                合計: ¥{selectedTotal.toLocaleString()}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" onClick={handleBatchApprove}>
                <CheckCircle size={16} />
                一括承認
              </Button>
              <Button variant="secondary" onClick={() => setSelectedOrders([])}>
                選択解除
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 緊急度別発注リスト */}
      {['immediate', 'today', 'this_week', 'next_week'].map(urgency => {
        const items = groupedSuggestions[urgency];
        if (!items || items.length === 0) return null;
        
        return (
          <Card key={urgency} className="overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between" 
              style={{ 
                borderColor: colors.ui.border,
                backgroundColor: urgency === 'immediate' ? colors.status.error + '10' : 'transparent'
              }}
            >
              <div className="flex items-center gap-2">
                <Clock size={18} style={{ color: getUrgencyColor(urgency) }} />
                <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                  {getUrgencyText(urgency)}の発注
                </h3>
                <Badge variant={urgency === 'immediate' ? 'error' : 'secondary'}>
                  {items.length}件
                </Badge>
              </div>
            </div>
            
            <div className="divide-y" style={{ borderColor: colors.ui.border }}>
              {items.map(suggestion => {
                const stock = stockItems.find(s => s.skuId === suggestion.skuId);
                const status = orderStatus[suggestion.skuId];
                
                return (
                  <div 
                    key={suggestion.skuId}
                    className={`p-4 ${status === 'approved' ? 'bg-green-50' : status === 'rejected' ? 'bg-red-50' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* チェックボックス */}
                      {!status && (
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(suggestion.skuId)}
                          onChange={() => handleSelectOrder(suggestion.skuId)}
                          className="mt-1"
                        />
                      )}
                      
                      {/* 商品情報 */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-sm" style={{ color: colors.text.primary }}>
                              {suggestion.skuName}
                            </h4>
                            <span className="text-xs" style={{ color: colors.text.secondary }}>
                              {suggestion.skuId}
                            </span>
                          </div>
                          {status && (
                            <Badge 
                              variant={status === 'approved' ? 'success' : 'error'}
                            >
                              {status === 'approved' ? '承認済' : '却下'}
                            </Badge>
                          )}
                        </div>
                        
                        {/* 発注詳細 */}
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span style={{ color: colors.text.secondary }}>現在庫</span>
                            <div className="font-medium">{suggestion.currentStock}</div>
                          </div>
                          <div>
                            <span style={{ color: colors.text.secondary }}>発注数</span>
                            <div className="font-medium" style={{ color: colors.accent.blue }}>
                              {suggestion.suggestedQuantity}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: colors.text.secondary }}>予想金額</span>
                            <div className="font-medium">
                              ¥{suggestion.estimatedCost.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: colors.text.secondary }}>納品予定</span>
                            <div className="font-medium">
                              {new Date(suggestion.expectedDelivery).toLocaleDateString('ja-JP')}
                            </div>
                          </div>
                        </div>
                        
                        {/* 理由 */}
                        <div className="mt-2 p-2 rounded text-xs" style={{ backgroundColor: colors.ui.bgSecondary }}>
                          <span style={{ color: colors.text.secondary }}>理由: </span>
                          <span style={{ color: colors.text.primary }}>{suggestion.reason}</span>
                        </div>
                        
                        {/* 在庫推移予測 */}
                        {stock && (
                          <div className="mt-2 flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <TrendingUp size={12} style={{ color: colors.text.secondary }} />
                              <span style={{ color: colors.text.secondary }}>日次需要:</span>
                              <span className="font-medium">{stock.dailyDemand}個</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar size={12} style={{ color: colors.text.secondary }} />
                              <span style={{ color: colors.text.secondary }}>リードタイム:</span>
                              <span className="font-medium">{stock.leadTimeDays}日</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* アクションボタン */}
                      {!status && (
                        <div className="flex gap-2">
                          <Button 
                            size="small" 
                            variant="primary"
                            onClick={() => handleApproveOrder(suggestion.skuId)}
                          >
                            承認
                          </Button>
                          <Button 
                            size="small" 
                            variant="secondary"
                            onClick={() => handleRejectOrder(suggestion.skuId)}
                          >
                            却下
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default OrderProposal;
